export interface Slide {
  id: number
  type: string
  title: string
  content?: any
}

export interface TalkingPoint {
  slideId: number
  points: string[]
  keyMessages: string[]
  timing: string
}

export const slides: Slide[] = [
  {
    id: 1,
    type: 'title-hook',
    title: 'Title & The Hook',
    content: {
      headline: 'Stop Bleeding Leads: The AI Blueprint for Home Service Pros',
      subHeadline:
        "How the top 10% of contractors are stealing your jobs while you're under a sink or on a roof.",
    },
  },
  {
    id: 2,
    type: 'no-bs-promise',
    title: 'The No Bullshit Promise',
    content: {
      headline: 'Why You Are Here (And Yes, This is a Pitch)',
      bullets: [
        'Let\'s skip the fake "I\'m just here to give free info" routine. You are business owners; you know how this works.',
        "I'm here to show you exactly where your business is leaking revenue.",
        'By the end of this 30 minutes, I want you to buy one of my systems or let me do a Lead Audit for your business.',
        "Why? Because what I'm about to show you will absolutely make you more money.",
      ],
    },
  },
  {
    id: 3,
    type: 'meet-host',
    title: 'Meet Your Host',
    content: {
      headline: 'Who is Kevin Rogers?',
      bullets: [
        'Owner, Viridia Analytics: (North Phoenix, AZ)',
        "5+ Years in Construction & Remodeling: I know what it's like to be on the job site, dealing with dispatch, tools, and clients.",
        '8+ Years in Lead Gen & Scaling Businesses: Expertise in CPC ads, Yelp, and Zillow for local businesses and real estate.',
      ],
    },
  },
  {
    id: 4,
    type: 'market-reality',
    title: 'The Market Reality',
    content: {
      headline: 'You Are Already Paying For These Leads',
      highlight:
        'Over 45% of high-intent traffic originates from active online searches.',
      text: 'Home service pros excel at their trades but hemorrhage money due to fragmented lead sources and poor response times.',
      source: 'Source: Viridia Analytics Home Services Growth Blueprint',
      data: [
        { name: 'Yelp', value: 35, color: '#0FB07D' },
        { name: 'Google LSA', value: 30, color: '#017C6D' },
        { name: 'SEO/Organic', value: 15, color: '#1C1C1C' },
        { name: 'Referrals', value: 10, color: '#4D4D4D' },
        { name: 'Social Media', value: 10, color: '#9CA3AF' },
      ],
    },
  },
  {
    id: 5,
    type: 'conversion-cliff',
    title: 'The 5-Minute Conversion Cliff',
    content: {
      headline: 'The 5-Minute Conversion Cliff',
      boldText:
        "Your chance of booking a job drops by 80% if you don't respond within 5 minutes.",
      text: 'When you are under a sink or on a roof, the next guy on Google is getting the job.',
      source: 'Source: Lead Response Management Study / Viridia Data',
      data: [
        { time: '1 Min', conversion: 100 },
        { time: '5 Mins', conversion: 20 },
        { time: '10 Mins', conversion: 10 },
        { time: '30 Mins', conversion: 5 },
        { time: '1 Hour', conversion: 2 },
        { time: '24 Hours', conversion: 0 },
      ],
    },
  },
  {
    id: 6,
    type: 'competitor-gap',
    title: 'The Competitor Gap Analysis',
    content: {
      headline: 'What The Top 10% Are Doing Right Now',
      bullets: [
        'Average Contractor: Relying on voicemails, manual callbacks, and sticky notes.',
        'Top 10%: Leveraging AI Receptionists and instant lead auto-responders.',
      ],
      source: 'Source: Local Services Technology Adoption Matrix',
      data: [
        { category: 'AI Chat', average: 5, top10: 85 },
        { category: 'Auto-Responders', average: 15, top10: 95 },
        { category: 'CRM Usage', average: 30, top10: 100 },
        { category: 'Online Booking', average: 20, top10: 90 },
      ],
    },
  },
  {
    id: 7,
    type: 'frankenstein',
    title: 'The Frankenstein Business Problem',
    content: {
      headline: "You Don't Just Have a Lead Problem, You Have a System Problem",
      bullets: [
        'Paying for leads on one platform.',
        'Tracking customers in a messy spreadsheet or notebook.',
        'Chasing down invoices in a separate app.',
        "Ignoring social media because you don't have the time.",
      ],
    },
  },
  {
    id: 8,
    type: 'ai-solution',
    title: 'The Solution: 24/7 AI Receptionist',
    content: {
      headline: 'How Viridia Stops the Bleeding',
      steps: [
        {
          title: 'The Trigger',
          description: 'Missed call, Web Form, Facebook message',
        },
        {
          title: 'Instant AI Engagement',
          description:
            'AI texts back instantly, triages the issue: "Residential or Commercial?"',
        },
        {
          title: 'Closed & Booked',
          description: 'AI syncs to your calendar and notifies dispatch',
        },
      ],
      source: 'Source: Viridia 24/7 AI Workflow Architecture',
    },
  },
  {
    id: 9,
    type: 'one-system',
    title: 'One System to Rule Them All',
    content: {
      headline: 'Your Entire Business Under One Roof',
      features: [
        'Automated Lead Responders & AI Receptionist (Never miss a job)',
        'Complete Customer Database (CRM) (Track every homeowner)',
        'Revenue Tracking & Invoicing (Know your numbers, get paid faster)',
        'Multi-Channel Social Media Planner (Look active everywhere, automatically)',
        'Automated Review Requests (Dominate local SEO)',
      ],
    },
  },
  {
    id: 10,
    type: 'case-study-proof',
    title: 'Proof It Works: Phoenix Landscaper',
    content: {
      headline: "We Nearly Doubled Their Competitor's Conversion Rate",
      subHeadline:
        'Real results from our automated marketing system over 6 months.',
      stats: [
        {
          value: '39.75%',
          label: 'Conversion Rate',
          detail: 'vs 22.3% industry average',
        },
        {
          value: '$41.87',
          label: 'Cost Per Lead',
          detail: '19% lower than top competitor',
        },
        {
          value: '120+',
          label: 'Leads Generated',
          detail: 'Last 30 days (as of 01/26)',
        },
        {
          value: '6',
          label: 'Month Partnership',
          detail: 'Ongoing optimization & growth',
        },
      ],
      comparisonLabel: 'Conversion Rate: Viridia Client vs Industry Average',
      comparisonClient: 39.75,
      comparisonIndustry: 22.3,
      source: 'Source: case-study.viridiaanalytics.com',
    },
  },
  {
    id: 11,
    type: 'pitch',
    title: 'The Pitch',
    content: {
      headline: 'Stop Losing Money. Start Scaling.',
      link: 'www.viridiaanalytics.com/pricing',
      text: 'We build it. We integrate it. You just show up to the jobs.',
      urgency:
        'Every day you wait, you are paying for leads that go to your competitors.',
    },
  },
  {
    id: 12,
    type: 'lead-audit',
    title: 'Not Ready to Pull the Trigger?',
    content: {
      headline: 'Claim Your Free Lead Audit',
      bullets: [
        'Let me look under the hood of your business.',
        'I will audit your current lead response times and local SEO presence.',
        "I'll show you exactly how much revenue is slipping through the cracks.",
        'Scan the QR code or click the link in the chat to book 15 minutes with me.',
      ],
      callToAction: 'Scan the QR code or click the link in the chat',
    },
  },
  {
    id: 13,
    type: 'qa-final',
    title: 'Q&A / Action Step',
    content: {
      headline: "Let's Build Your Machine. Questions?",
      contact: {
        name: 'Kevin Rogers',
        website: 'ViridiaAnalytics.com/pricing',
        phone: '623-300-0708',
        email: 'kevin@viridiaanalytics.com',
      },
    },
  },
]

export const talkingPoints: TalkingPoint[] = [
  {
    slideId: 1,
    timing: '2 minutes',
    keyMessages: [
      'Hook the audience',
      'Identify the core pain point immediately',
    ],
    points: [
      "Welcome everyone. Let's get right to it. You're here because you're paying for leads, but you're tired of playing phone tag, tired of losing jobs to the guy who called back three minutes faster, and tired of disjointed software. Today, I'm going to show you exactly how to fix that.",
    ],
  },
  {
    slideId: 2,
    timing: '2 minutes',
    keyMessages: [
      'Establish radical transparency',
      'Set the agenda for a pitch',
    ],
    points: [
      "I respect your time too much to pretend I'm not here to earn your business. I'm going to show you exactly how the top competitors in your market are operating. If you like it, we can partner up at the end. If not, you'll still leave knowing exactly what you need to fix.",
    ],
  },
  {
    slideId: 3,
    timing: '2 minutes',
    keyMessages: ['Build credibility', 'Show industry-specific understanding'],
    points: [
      "I'm not just a tech guy trying to sell software. I've spent 5+ years directly in construction and remodeling. I know exactly how hard it is to answer a ringing phone when you've got your hands full on a job site. I also spent the last 8 years mastering lead generation. I built Viridia Analytics to bridge that exact gap.",
    ],
  },
  {
    slideId: 4,
    timing: '3 minutes',
    keyMessages: [
      'Highlight existing ad spend',
      'Identify the real problem (capture, not traffic)',
    ],
    points: [
      "You are already spending money on Yelp, Google Local Service Ads, and SEO. Over 45% of your best leads are actively searching for you. But getting the click isn't your problem. Capturing the lead when they click is where the money is burning.",
    ],
  },
  {
    slideId: 5,
    timing: '4 minutes',
    keyMessages: ['The 5-minute rule', 'Quantify the loss of slow response'],
    points: [
      "This is the most important slide today. Look at this drop-off. If a homeowner has a busted AC or a leaking pipe, they aren't leaving a voicemail and waiting hours for you to finish your current job. If you don't engage them in 5 minutes, your booking rate plummets by 80%. You just paid for a lead, and handed the cash to your competitor.",
    ],
  },
  {
    slideId: 6,
    timing: '3 minutes',
    keyMessages: [
      'Compare to top competitors',
      'Introduce automation as the differentiator',
    ],
    points: [
      "Your top-tier local competitors aren't working harder than you, they are leveraging automation. They have completely eliminated the phone tag game. While average contractors are checking voicemails at 6 PM, the top 10% already booked the job at 2 PM using AI.",
    ],
  },
  {
    slideId: 7,
    timing: '3 minutes',
    keyMessages: ['Identify operational chaos', 'Fragmented tools waste time'],
    points: [
      "It's not just about missing calls. It's about the headache of running a Frankenstein business. You have your leads over here, your schedule over there, your invoices somewhere else, and social media is totally ignored. It's exhausting.",
    ],
  },
  {
    slideId: 8,
    timing: '4 minutes',
    keyMessages: [
      'Explain the AI workflow',
      'Show how it works while they sleep/work',
    ],
    points: [
      "Here is how we fix it. A lead comes in. Instead of going to voicemail, our AI instantly texts them. It greets them, asks qualifying questions—like what kind of AC unit they have or if it's an emergency—and then books them directly onto your calendar. It happens in seconds, whether you are asleep, driving, or on a job.",
    ],
  },
  {
    slideId: 9,
    timing: '3 minutes',
    keyMessages: ['Present the all-in-one solution', 'Highlight key features'],
    points: [
      "We don't just solve the missed call problem. We give you the entire engine. Revenue tracking, one-click invoicing, your whole customer database, and a tool to blast out your social media content across all channels—all in one single, easy-to-use dashboard.",
    ],
  },
  {
    slideId: 10,
    timing: '2 minutes',
    keyMessages: ['Show real proof', 'Let the numbers do the talking'],
    points: [
      "This isn't hypothetical. This is a real Phoenix landscaper we've been working with for 6 months. Their conversion rate hit 39.75% — the industry average is 22.3%. That's nearly double. Their cost per lead is $41.87, which is 19% lower than their top competitor. And in the last 30 days alone, they generated over 120 leads. This is what happens when you stop playing phone tag and let the system work for you.",
    ],
  },
  {
    slideId: 11,
    timing: '2 minutes',
    keyMessages: ['The direct pitch', 'Call to action to buy'],
    points: [
      'I told you I was going to pitch you, and here it is. Go to viridiaanalytics.com/pricing. Pick the package that fits your business size. We do the heavy lifting. We set up the AI, we connect it to your numbers, and we build the system. All you have to do is what you do best—show up and do the work.',
    ],
  },
  {
    slideId: 12,
    timing: '1 minute',
    keyMessages: ['Alternative low-friction offer', 'Free audit'],
    points: [
      "If you want to look at the numbers before you buy, I respect that. Scan this QR code. It will take you to my calendar. We'll do a 15-minute Lead Audit. I will show you exactly where you are losing money on the 5-minute cliff, and how we can patch the holes.",
    ],
  },
  {
    slideId: 13,
    timing: '1 minute',
    keyMessages: ['Open for Q&A', 'Final contact info'],
    points: [
      "That's it. 30 minutes, no fluff. Who has questions about how the AI handles complex customer inquiries? Drop them in the chat.",
    ],
  },
]
